import type { SubscriptionSummary } from '../../types/subscription';

/**
 * Near-zero hiding for the overview.
 *
 * A retired subscription keeps its cost basis and its realised result, so it MUST stay in every
 * all-time total — leaving it out is what made retiring a loss-making bot lift the headline by
 * that bot's whole loss with no money having moved. But an empty shell is noise in the grid and
 * charts, so the user can hide it visually.
 *
 * Hiding is therefore presentational ONLY. Callers pass the full set to every calculation and the
 * result of `visibleSubscriptions` to whatever renders rows.
 */

/**
 * What counts as "near zero". Fixed rather than per-user: the setting is a plain on/off, and a
 * number that has to be chosen is a decision the user should not have to make.
 */
export const NEAR_ZERO_HIDE_THRESHOLD = 2;

/**
 * Only RETIRED subscriptions can be hidden. An active bot sitting at ~0 is something to look at,
 * not something to tidy away — WLFI ran at 0.00 with a −182 result before it was retired.
 */
export const isHiddenNearZero = (
  subscription: SubscriptionSummary,
  enabled: boolean | undefined,
): boolean => {
  if (!enabled) return false;
  if (subscription.isActive) return false;

  return (subscription.totalBalanceInStable ?? 0) < NEAR_ZERO_HIDE_THRESHOLD;
};

export const visibleSubscriptions = (
  subscriptions: SubscriptionSummary[],
  enabled: boolean | undefined,
): SubscriptionSummary[] =>
  subscriptions.filter(
    (subscription) => !isHiddenNearZero(subscription, enabled),
  );

export type HiddenSummary = {
  count: number;
  /** Combined absolute profit of the hidden rows — what the visible rows do not account for. */
  profit: number;
  value: number;
};

/**
 * What was hidden, so the screen can say so. Without this the visible rows silently fail to add
 * up to the headline, which reads as a bug rather than a setting.
 */
export const hiddenSummary = (
  subscriptions: SubscriptionSummary[],
  enabled: boolean | undefined,
): HiddenSummary => {
  const hidden = subscriptions.filter((subscription) =>
    isHiddenNearZero(subscription, enabled),
  );

  return {
    count: hidden.length,
    profit: hidden.reduce(
      (acc, subscription) =>
        acc +
        ((subscription.totalBalanceInStable ?? 0) -
          (subscription.profits?.costBasis.stable ?? 0)),
      0,
    ),
    value: hidden.reduce(
      (acc, subscription) => acc + (subscription.totalBalanceInStable ?? 0),
      0,
    ),
  };
};
